import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	Button,
	Column,
	Conditional,
	Else,
	filterAndSortByQuery,
	FormattedText,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	Section,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { Icon } from "../../../../Components/Icon";
import { SearchField } from "../../../../Components/Search";
import {
	CourseAssessment,
	CourseAssessmentDetailQuery,
	CourseAssessmentStudentsListQuery,
	CourseSection,
	useCourseAssessmentDetailQuery,
	useCourseAssessmentStudentsListQuery,
	useSearchFormState,
} from "../../../../Utilities";
import {
	CourseAssessmentFormat,
	CourseAssessmentSubmissionFormat,
	CourseAssessmentTimingFormat,
	StudentFormat,
} from "../../../Format";
import { MediaAttachmentUtilities } from "../../../Utilities";
import {
	FileAttachmentsRow,
	LinkAttachmentsRow,
	MediaAttachmentModal,
	MediaAttachmentsSummary,
	VisualAttachmentsRow,
} from "../../MediaAttachment";
import { SubmissionStatusBadge } from "../SubmissionStatusBadge";
import { CourseAssessmentGradingForm } from "./GradingForm";
import styles from "./styles.module.css";

export type ControlledCourseAssessmentModalProps = {
	courseAssessmentID: CourseAssessment["id"];
	courseSectionID: CourseSection["id"];
	withBackButton?: boolean;
	backButtonText?: string;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ControlledCourseAssessmentModal({
	courseAssessmentID,
	courseSectionID,
	withBackButton = false,
	backButtonText = "Back",
	ifRef,
	onClose,
	className,
	style,
}: ControlledCourseAssessmentModalProps) {
	const { loading, data } = useCourseAssessmentDetailQuery({
		variables: { id: courseAssessmentID },
	});
	const courseAssessment = data?.courseAssessment;

	return (
		<Modal size="full" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ marginRight: "1.5rem" }}>
					<If condition={withBackButton}>
						{onClose && (
							<Button variant="link" size="small" onClick={onClose}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
									<Icon.ArrowLeft size={14} /> {backButtonText}
								</Row>
							</Button>
						)}
					</If>

					<Heading.H2 noMargin>
						{courseAssessment ? CourseAssessmentFormat.name(courseAssessment) : "Assessment"}
					</Heading.H2>
				</Column>
			</Modal.Header>
			<Modal.Body style={{ height: "100%", overflow: "hidden" }}>
				<Conditional>
					<If condition={!courseAssessment && loading}>Loading...</If>
					<Else>
						{!!courseAssessment && (
							<Row justify="spaced-start" style={{ height: "100%" }}>
								<CourseAssessmentSection
									courseAssessment={courseAssessment}
									courseSectionID={courseSectionID}
									style={{ width: "25rem", height: "100%", minHeight: 0, overflow: "scroll" }}
								/>
								<StudentsSection
									courseAssessment={courseAssessment}
									courseSectionID={courseSectionID}
									style={{ flexGrow: 1, height: "100%", minHeight: 0, overflow: "scroll" }}
								/>
							</Row>
						)}
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}

type ModalSectionProps = {
	courseAssessment: CourseAssessmentDetailQuery["courseAssessment"];
	courseSectionID: CourseSection["id"];
} & Partial<StyleProps>;

function CourseAssessmentSection({ courseAssessment, courseSectionID, style }: ModalSectionProps) {
	const timing = courseAssessment.courseAssessmentTimings.find((e) => e.courseSection.id === courseSectionID);

	return (
		<Section className={styles["main-section"]} style={style}>
			<Section.Header>
				<Heading.H3 noMargin>Assessment Details</Heading.H3>
			</Section.Header>
			<Section.Body>
				<Column justify="space-between" style={{ height: "100%" }}>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Row justify="spaced-start" align="center" className={styles["summary-item"]}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
								<Icon.NotePencil size={20} />
								<strong>Assessment Name</strong>
							</Row>
							<span className={styles["summary-item__value"]}>{CourseAssessmentFormat.name(courseAssessment)}</span>
						</Row>
						{!!timing && (
							<Row justify="spaced-start" align="center" className={styles["summary-item"]}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
									<Icon.CalendarBlank size={20} />
									<strong>Due Date</strong>
								</Row>
								<span className={styles["summary-item__value"]}>
									{CourseAssessmentTimingFormat.Fields.dueOn(timing?.dueOn, { weekday: "short" })}
								</span>
							</Row>
						)}
						<Row justify="spaced-start" align="center" className={styles["summary-item"]}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
								<Icon.Exam size={20} />
								<strong>Total Points</strong>
							</Row>
							<span className={styles["summary-item__value"]}>
								{CourseAssessmentFormat.Fields.totalPoints(courseAssessment.totalPoints)}
							</span>
						</Row>

						<If condition={!!courseAssessment.description}>
							<FormattedText className={styles["assessment-description"]}>{courseAssessment.description}</FormattedText>
						</If>
					</Column>

					<div style={{ marginTop: "1rem" }}>
						<AttachmentsView courseAssessment={courseAssessment} />
					</div>
				</Column>
			</Section.Body>
		</Section>
	);
}

type AttachmentsViewProps = {
	courseAssessment: CourseAssessmentDetailQuery["courseAssessment"];
};

function AttachmentsView({ courseAssessment }: AttachmentsViewProps) {
	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() =>
			MediaAttachmentUtilities.splitAndExtractMediaAttachments(
				courseAssessment.mediaAttachments,
				courseAssessment.description,
			),
		[courseAssessment.mediaAttachments, courseAssessment.description],
	);

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentModal.useModal();

	return (
		<Column justify="spaced-start" verticalSpacing="1rem">
			<If condition={visualAttachments.length > 0}>
				<VisualAttachmentsRow attachments={visualAttachments} onClickAttachment={onClickMediaAttachment} />

				<Modal.If condition={isMediaAttachmentModalOpen}>
					<MediaAttachmentModal
						mediaAttachments={visualAttachments}
						initialIndex={mediaAttachmentModalIndex}
						backButtonText="Back to Announcement"
						onClose={closeMediaAttachmentModal}
					/>
				</Modal.If>
			</If>
			<If condition={fileAttachments.length > 0}>
				<FileAttachmentsRow attachments={fileAttachments} />
			</If>
			<If condition={linkAttachments.length > 0}>
				<LinkAttachmentsRow attachments={linkAttachments} />
			</If>
		</Column>
	);
}

const columns = ["student", "submissionStatus", "submissionDate", "points", "attachments", "notes", "edit"];

const columnWidths = {
	edit: "3rem",
};

function StudentsSection({ courseAssessment, courseSectionID, style }: ModalSectionProps) {
	const searchFormState = useSearchFormState();
	const { search } = searchFormState.formValues;

	const { loading, data, error } = useCourseAssessmentStudentsListQuery({
		variables: {
			courseAssessmentID: courseAssessment.id,
			filters: { studentCourseSectionEnrollments: [{ courseSectionIDEQ: courseSectionID }] },
		},
	});
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "studentConnection" });

	const filteredStudentEdges = React.useMemo(() => {
		if (!data) {
			return [];
		}
		return filterAndSortByQuery(search, data.studentConnection.edges, (e) => StudentFormat.name(e.node));
	}, [search, data]);

	return (
		<Section className={styles["main-section"]} style={style}>
			<Section.Header>
				<Heading.H3 noMargin>Students</Heading.H3>
			</Section.Header>
			<Section.Body>
				<Column justify="spaced-start">
					<SearchField formState={searchFormState} name="search" />

					<Table columns={columns} columnWidths={columnWidths} className={styles["table"]}>
						<Table.Header>
							<Table.Row>
								<Table.Heading column="student" className={styles["table__heading"]}>
									Student
								</Table.Heading>
								<Table.Heading column="submissionStatus" className={styles["table__heading"]}>
									Status
								</Table.Heading>
								<Table.Heading column="submissionDate" className={styles["table__heading"]}>
									Turn In Date
								</Table.Heading>
								<Table.Heading column="points" className={styles["table__heading"]}>
									Points
								</Table.Heading>
								<Table.Heading column="attachments" className={styles["table__heading"]}>
									Attachments
								</Table.Heading>
								<Table.Heading column="notes" className={styles["table__heading"]}>
									Comments
								</Table.Heading>
								<Table.Heading column="edit" className={styles["table__heading"]}></Table.Heading>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							<Conditional>
								<If condition={filteredStudentEdges.length > 0}>
									{filteredStudentEdges.map(({ node: student }) => (
										<StudentRow key={student.id} student={student} courseAssessment={courseAssessment} />
									))}
								</If>
								<Else>
									<Table.Row>
										<Table.Cell column={columns[0]} colspan={columns.length} className={styles["table__cell"]}>
											<Conditional>
												<If condition={queryStatus === "error"}>Something went wrong</If>
												<If condition={queryStatus === "loading"}>Loading...</If>
												<If condition={filteredStudentEdges.length === 0}>No students</If>
											</Conditional>
										</Table.Cell>
									</Table.Row>
								</Else>
							</Conditional>
						</Table.Body>
					</Table>
				</Column>
			</Section.Body>
		</Section>
	);
}

type StudentRowProps = {
	student: CourseAssessmentStudentsListQuery["studentConnection"]["edges"][0]["node"];
	courseAssessment: Pick<CourseAssessment, "name" | "totalPoints">;
};

function StudentRow({ student, courseAssessment }: StudentRowProps) {
	const submission = student.courseAssessmentSubmissions[0] as
		| CourseAssessmentStudentsListQuery["studentConnection"]["edges"][0]["node"]["courseAssessmentSubmissions"][0]
		| undefined;
	const mark = student.courseAssessmentMarks[0] as
		| CourseAssessmentStudentsListQuery["studentConnection"]["edges"][0]["node"]["courseAssessmentMarks"][0]
		| undefined;

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Table.Row>
				<Table.Cell column="student" className={styles["table__cell"]}>
					{StudentFormat.name(student)}
				</Table.Cell>
				<Table.Cell
					column="submissionStatus"
					className={styles["table__cell"]}
					style={{ paddingTop: 0, paddingBottom: 0 }}
				>
					<Column justify="center" style={{ height: "100%" }}>
						<SubmissionStatusBadge courseAssessmentSubmission={submission} courseAssessmentMark={mark} />
					</Column>
				</Table.Cell>
				<Table.Cell column="submissionDate" className={styles["table__cell"]}>
					{submission ? CourseAssessmentSubmissionFormat.Fields.submissionDate(submission.submissionDate) : "-"}
				</Table.Cell>
				<Table.Cell column="points" className={styles["table__cell"]}>
					{mark && mark.isExcused ? "-" : `${mark ? mark.points : "-"}/${courseAssessment.totalPoints}`}
				</Table.Cell>
				<Table.Cell column="attachments" className={styles["table__cell"]}>
					{submission && submission.mediaAttachments.length > 0 ? (
						<MediaAttachmentsSummary mediaAttachments={submission.mediaAttachments} />
					) : (
						"-"
					)}
				</Table.Cell>
				<Table.Cell column="notes" className={styles["table__cell"]}>
					{mark && mark.notes ? mark.notes : "-"}
				</Table.Cell>
				<Table.Cell column="edit" className={styles["table__cell"]} style={{ paddingTop: 0, paddingBottom: 0 }}>
					<Column justify="center" style={{ height: "100%" }}>
						<Button variant="link" size="small" onClick={openModal}>
							Edit
						</Button>
					</Column>
				</Table.Cell>
			</Table.Row>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ width: "44.5rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>{StudentFormat.name(student)}</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<Column justify="spaced-start" verticalSpacing="1rem">
							<Row justify="spaced-start" align="center" horizontalSpacing="2.25rem">
								<Row justify="spaced-start" align="center" style={{ minWidth: 0 }}>
									<Row
										justify="spaced-start"
										align="center"
										horizontalSpacing="0.375rem"
										className={styles["grading-modal__summary-label"]}
									>
										<Icon.NotePencil size={20} /> Assessment Name
									</Row>
									<TruncatedText className={styles["grading-modal__summary-value"]}>
										{CourseAssessmentFormat.name(courseAssessment)}
									</TruncatedText>
								</Row>
								<Row justify="spaced-start" align="center">
									<Row
										justify="spaced-start"
										align="center"
										horizontalSpacing="0.375rem"
										className={styles["grading-modal__summary-label"]}
									>
										<Icon.Exam size={20} /> Total Points
									</Row>
									<span className={styles["grading-modal__summary-value"]}>
										{CourseAssessmentFormat.Fields.totalPoints(courseAssessment.totalPoints)}
									</span>
								</Row>
							</Row>

							<CourseAssessmentGradingForm totalPoints={courseAssessment.totalPoints} />
						</Column>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}
